import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaStore,
  FaCheckCircle,
  FaMapPin,
  FaIdCard,
  FaArrowRight,
  FaUserCircle,
  FaEye,
  FaUtensils,
  FaPen,
  FaMapMarkerAlt,
  FaQuestionCircle,
  FaBookOpen,
  FaExternalLinkAlt,
  FaStar,
  FaClock,
  FaHandshake,
  FaTicketAlt,
  FaSignOutAlt,
  FaMobileAlt,
  FaBell,
  FaSpinner,
  FaExclamationTriangle,
  FaPhone,
  FaEnvelope,
  FaTimes,
} from "react-icons/fa";

import { getData, postData } from '../../../Services/Fetchnodeservices';

// 🔥 Define serverURL and IMAGE_BASE_URL
const serverURL = 'https://new.zaptodoor.com';
const IMAGE_BASE_URL = `${serverURL}/uploads/`;

const ProfileCard = ({ isDark = false }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(3);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    rating: 0
  });
  const [openTicketCount, setOpenTicketCount] = useState(0);
  
  // 🔥 State for logo preview modal
  const [logoPreview, setLogoPreview] = useState(null);

  // 🎨 ZAPTODOOR ORANGE THEME CONFIGURATION
  const theme = {
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    hoverBg: isDark ? 'rgba(249, 115, 22, 0.1)' : '#ffedd5',
    shadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 1px 3px 0 rgba(249, 115, 22, 0.1)',
    shadowLg: isDark ? '0 10px 15px -3px rgba(0, 0, 0, 0.4)' : '0 10px 15px -3px rgba(249, 115, 22, 0.15)',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryDark: '#ea580c',
    primaryGradient: isDark 
      ? 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)'
      : 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
  };

  const getUserDataFromStorage = () => {
    try {
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) return JSON.parse(storedUserData);
      
      const userDataObj = {
        id: localStorage.getItem('restaurantId') || localStorage.getItem('userId'),
        email: localStorage.getItem('userEmail'),
        name: localStorage.getItem('userName') || localStorage.getItem('owner_name'),
        owner_name: localStorage.getItem('owner_name'),
        restaurantId: localStorage.getItem('restaurantId'),
        phone: localStorage.getItem('userPhone'),
      };
      return userDataObj.id ? userDataObj : null;
    } catch (error) {
      console.error("Error getting user data:", error);
      return null;
    }
  };

  // 🔥 FIX: Updated getImageUrl to use IMAGE_BASE_URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    // Remove leading slashes
    const cleanPath = imagePath.replace(/^\/+/, '');
    return `${IMAGE_BASE_URL}${cleanPath}`;
  };

  // 🔥 Open logo preview
  const openLogoPreview = (imageUrl) => {
    if (imageUrl) {
      setLogoPreview(imageUrl);
    }
  };

  // 🔥 Close logo preview
  const closeLogoPreview = () => {
    setLogoPreview(null);
  };

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        const storedData = getUserDataFromStorage();
        if (storedData) {
          setUserData(storedData);
          const restaurantId = storedData?.restaurantId || storedData?.id;
          if (restaurantId) {
            await Promise.all([fetchOrderStats(restaurantId), fetchOpenTicketCount(restaurantId)]);
          }
        } else {
          setError('Please login again');
        }
      } catch (err) {
        setError(err.message || "Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };
    loadUserData();
  }, []);

  const fetchOrderStats = async (restaurantId) => {
    try {
      const result = await postData('vanderorders/orders/list', { designation_id: restaurantId });
      if (result.success) {
        const orders = result.orders || [];
        const deliveredOrders = orders.filter(o => 
          o.order_status_user === 'delivered' || o.order_status_restro === 'delivered' || o.order_status_user === 'completed'
        );
        const totalRevenue = deliveredOrders.reduce((sum, order) => {
          return sum + (parseFloat(order.final_amount) || parseFloat(order.business_amount) || 0);
        }, 0);
        const ratings = orders.filter(o => o.rating).map(o => parseFloat(o.rating));
        const avgRating = ratings.length > 0 
          ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10 
          : 4.5;
        setStats({ totalRevenue, totalOrders: deliveredOrders.length, rating: avgRating });
      }
    } catch (error) {
      console.error("Error fetching order stats:", error);
    }
  };

  const fetchOpenTicketCount = async (restaurantId) => {
    try {
      const result = await getData(
        `supportRoutes?user_table=restaurants&user_id=${encodeURIComponent(restaurantId)}`
      );
      if (result?.success) {
        const openCount = (result.data || []).filter(
          (ticket) => String(ticket.ticketstatus || '').toLowerCase() === 'open'
        ).length;
        setOpenTicketCount(openCount);
      }
    } catch (error) {
      console.error('Error fetching support ticket count:', error);
      setOpenTicketCount(0);
    }
  };

  const getKycProgress = () => {
    const checks = [
      userData?.is_verify_personalinfo === 1 || userData?.is_verify_personalinfo === '1',
      userData?.is_verify_docinfo === 1 || userData?.is_verify_docinfo === '1',
      userData?.is_verify_bankinfo === 1 || userData?.is_verify_bankinfo === '1',
      userData?.is_approved === 1 || userData?.is_approved === '1',
      userData?.status === 1 || userData?.status === '1'
    ];
    return Math.round((checks.filter(c => c).length / checks.length) * 100);
  };

  const handleNavigate = (path, state = null) => {
    state ? navigate(path, { state }) : navigate(path);
  };

  const handleAddressEdit = () => {
    const addressData = {
      address: userData?.address || '',
      city: userData?.city || '',
      state: userData?.state || '',
      postal: userData?.postal || '',
      latitude: userData?.latitude || '',
      longitude: userData?.longitude || '',
      landmark: userData?.landmark || '',
      restaurantId: userData?.id || userData?.restaurantId || '',
      restaurantName: userData?.organisation_name || userData?.restaurant_name || userData?.name || '',
    };
    navigate('/dashboard/EditAddress', { state: { addressData } });
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/VenderLogin";
  };

  const getSafeData = () => ({
    storeName: userData?.organisation_name || userData?.restaurant_name || userData?.name || "Store Name",
    city: userData?.city || "City",
    state: userData?.state || "State",
    shiftStart: userData?.shift_start || "10:00",
    shiftEnd: userData?.shift_end || "03:00",
    isApproved: userData?.is_approved === 1 || userData?.is_approved === '1',
    ownerName: userData?.owner_name || userData?.name || "Owner",
    // 🔥 FIX: Use 'pic' as the primary image source
    storeImage: userData?.pic || userData?.r_logo || userData?.profileImage,
    phone: userData?.phone || userData?.contact || userData?.mobile || userData?.owner_mobile || '',
    email: userData?.email || '',
    address: userData?.address || '',
    isMouAccepted: userData?.is_mou === 1 || userData?.is_mou === '1' || userData?.is_mou === true,
  });

  const data = getSafeData();

  // 🔥 Get the image URL for the logo
  const logoImageUrl = data.storeImage ? getImageUrl(data.storeImage) : null;

  // 🎨 Desktop-Optimized Styles with Orange Theme
  const styles = {
    pageContainer: {
      minHeight: "100vh",
      backgroundColor: theme.bg,
      color: theme.text,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      padding: "32px",
      transition: "background-color 0.3s ease, color 0.3s ease",
    },
    mainWrapper: {
      maxWidth: "1200px",
      margin: "0 auto",
      display: "flex",
      flexDirection: "column",
      gap: "24px",
    },
    headerCard: {
      background: theme.primaryGradient,
      borderRadius: "16px",
      padding: "32px",
      color: "white",
      position: "relative",
      overflow: "hidden",
      boxShadow: theme.shadowLg,
    },
    headerTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
    headerTitle: {
      fontSize: "14px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "1px",
      opacity: 0.8,
    },
    notificationBtn: {
      position: "relative",
      background: "rgba(255,255,255,0.15)",
      border: "1px solid rgba(255,255,255,0.2)",
      borderRadius: "10px",
      width: "44px",
      height: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontSize: "1.1rem",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    notificationBadge: {
      position: "absolute",
      top: "-6px",
      right: "-6px",
      backgroundColor: "#ef4444",
      color: "white",
      fontSize: "11px",
      fontWeight: "700",
      width: "22px",
      height: "22px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "2px solid #f97316",
    },
    storeInfo: {
      display: "flex",
      alignItems: "center",
      gap: "24px",
    },
    storeAvatar: {
      width: "80px",
      height: "80px",
      background: "white",
      borderRadius: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.primary,
      fontSize: "2.5rem",
      boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
      overflow: "hidden",
      flexShrink: 0,
      cursor: "pointer",
      position: "relative",
      transition: "transform 0.2s ease",
    },
    storeAvatarHover: {
      transform: "scale(1.05)",
    },
    storeAvatarOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.4)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontSize: "20px",
      opacity: 0,
      transition: "opacity 0.2s ease",
      borderRadius: "16px",
    },
    storeAvatarImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    storeDetails: {
      flex: 1,
    },
    storeName: {
      fontSize: "28px",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "8px",
    },
    storeStatus: {
      fontSize: "15px",
      opacity: 0.9,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "8px",
    },
    storeRating: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      fontSize: "14px",
      opacity: 0.9,
    },
    dashboardGrid: {
      display: "grid",
      gridTemplateColumns: "2fr 1fr",
      gap: "24px",
    },
    card: {
      backgroundColor: theme.cardBg,
      borderRadius: "16px",
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow,
      padding: "24px",
      transition: "all 0.3s ease",
    },
    statsRow: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "16px",
      marginBottom: "24px",
    },
    statItem: {
      backgroundColor: theme.cardBg,
      padding: "20px",
      borderRadius: "12px",
      border: `1px solid ${theme.border}`,
      textAlign: "center",
      transition: "all 0.2s",
    },
    statValue: {
      fontSize: "24px",
      fontWeight: "700",
      color: theme.text,
      marginBottom: "4px",
    },
    statLabel: {
      fontSize: "13px",
      fontWeight: "500",
      color: theme.textSecondary,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    sectionTitle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "16px",
      paddingBottom: "12px",
      borderBottom: `1px solid ${theme.border}`,
    },
    sectionTitleLeft: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "16px",
      fontWeight: "600",
      color: theme.text,
    },
    menuItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px",
      borderRadius: "12px",
      transition: "all 0.2s ease",
      cursor: "pointer",
      marginBottom: "8px",
      border: `1px solid transparent`,
      background: "transparent",
      width: "100%",
      textAlign: "left",
    },
    menuLeft: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    menuIconWrapper: {
      width: "44px",
      height: "44px",
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.1rem",
      flexShrink: 0,
    },
    menuIconPrimary: { backgroundColor: isDark ? 'rgba(249, 115, 22, 0.15)' : '#fff7ed', color: theme.primary },
    menuIconSecondary: { backgroundColor: isDark ? 'rgba(234, 88, 12, 0.15)' : '#ffedd5', color: theme.primaryDark },
    menuIconSuccess: { backgroundColor: isDark ? 'rgba(34, 197, 94, 0.15)' : '#f0fdf4', color: '#16a34a' },
    menuLabel: {
      fontWeight: "600",
      fontSize: "15px",
      color: theme.text,
      marginBottom: "2px",
    },
    menuDesc: {
      fontSize: "13px",
      color: theme.textSecondary,
    },
    menuAction: {
      backgroundColor: theme.hoverBg,
      color: theme.text,
      fontSize: "13px",
      fontWeight: "600",
      padding: "6px 14px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      border: `1px solid ${theme.border}`,
      cursor: "pointer",
    },
    infoItem: {
      padding: "16px",
      background: theme.hoverBg,
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      border: `1px solid ${theme.border}`,
    },
    supportCard: {
      backgroundColor: theme.hoverBg,
      borderRadius: "12px",
      padding: "20px",
      textAlign: "center",
      transition: "all 0.2s",
      cursor: "pointer",
      border: `1px solid ${theme.border}`,
      marginBottom: "12px",
    },
    logoutBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      width: "100%",
      background: isDark ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2',
      border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.2)' : '#fecaca'}`,
      color: "#ef4444",
      fontWeight: "600",
      fontSize: "15px",
      padding: "14px",
      borderRadius: "12px",
      transition: "all 0.2s",
      cursor: "pointer",
      marginTop: "16px",
    },
    kycBanner: {
      borderRadius: "12px",
      padding: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      border: `1px solid ${data.isApproved ? (isDark ? 'rgba(34, 197, 94, 0.3)' : '#bbf7d0') : (isDark ? 'rgba(249, 115, 22, 0.3)' : '#fed7aa')}`,
      marginBottom: "24px",
      position: "relative",
      overflow: "hidden",
      cursor: "pointer",
      transition: "all 0.3s ease",
      background: data.isApproved 
        ? (isDark ? 'rgba(34, 197, 94, 0.1)' : '#f0fdf4') 
        : (isDark ? 'rgba(249, 115, 22, 0.1)' : '#fff7ed'),
    },
    kycProgress: {
      position: "absolute",
      bottom: 0,
      left: 0,
      height: "3px",
      transition: "all 0.5s ease",
      backgroundColor: data.isApproved ? "#22c55e" : theme.primary,
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      gap: "16px",
    },
    // 🔥 Logo Preview Modal Styles
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.85)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      cursor: "pointer",
      backdropFilter: "blur(4px)",
    },
    modalImage: {
      maxWidth: "90%",
      maxHeight: "90%",
      borderRadius: "12px",
      objectFit: "contain",
      boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
    },
    modalClose: {
      position: "absolute",
      top: "24px",
      right: "24px",
      background: "rgba(255,255,255,0.15)",
      border: "1px solid rgba(255,255,255,0.2)",
      borderRadius: "50%",
      width: "48px",
      height: "48px",
      color: "#ffffff",
      fontSize: "24px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      backdropFilter: "blur(8px)",
    },
  };

  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingContainer}>
          <FaSpinner style={{ animation: "spin 1s linear infinite", fontSize: "2.5rem", color: theme.primary }} />
          <div style={{ color: theme.textSecondary, fontSize: "1rem" }}>Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.pageContainer}>
        <div style={{ ...styles.card, maxWidth: "500px", margin: "100px auto", textAlign: "center", padding: "40px" }}>
          <FaExclamationTriangle style={{ fontSize: "3rem", color: "#ef4444", marginBottom: "16px" }} />
          <h3 style={{ color: theme.text, marginBottom: "8px" }}>Failed to load profile</h3>
          <p style={{ color: theme.textSecondary, marginBottom: "24px" }}>{error}</p>
          <button onClick={() => window.location.reload()} style={{ padding: "10px 24px", background: theme.primaryGradient, color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <div style={styles.mainWrapper}>
        
        {/* Header Card */}
        <div style={styles.headerCard}>
          <div style={styles.headerTop}>
            <span style={styles.headerTitle}>Store Profile Dashboard</span>
            <button style={styles.notificationBtn}>
              <FaBell />
              {notifications > 0 && <span style={styles.notificationBadge}>{notifications}</span>}
            </button>
          </div>

          <div style={styles.storeInfo}>
            {/* 🔥 Store Avatar with click to preview */}
            <div 
              style={styles.storeAvatar}
              onClick={() => logoImageUrl && openLogoPreview(logoImageUrl)}
              onMouseEnter={(e) => {
                const overlay = e.currentTarget.querySelector('.avatar-overlay');
                const img = e.currentTarget.querySelector('.avatar-img');
                if (overlay) overlay.style.opacity = '1';
                if (img) img.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                const overlay = e.currentTarget.querySelector('.avatar-overlay');
                const img = e.currentTarget.querySelector('.avatar-img');
                if (overlay) overlay.style.opacity = '0';
                if (img) img.style.transform = 'scale(1)';
              }}
            >
              {logoImageUrl ? (
                <>
                  <img 
                    src={logoImageUrl} 
                    alt="Store Logo" 
                    className="avatar-img"
                    style={styles.storeAvatarImage}
                    onError={(e) => { 
                      e.target.style.display = 'none'; 
                      e.target.parentNode.innerHTML = '<FaStore style="font-size: 40px; color: #f97316;" />';
                    }} 
                  />
                  <div className="avatar-overlay" style={styles.storeAvatarOverlay}>
                    <FaEye size={24} />
                  </div>
                </>
              ) : (
                <FaStore style={{ fontSize: "40px" }} />
              )}
            </div>

            <div style={styles.storeDetails}>
              <div style={styles.storeName}>
                {data.storeName}
                {data.isApproved && <FaCheckCircle style={{ color: "#4ade80", fontSize: "20px" }} />}
                {data.isOpen && <span style={{ fontSize: "12px", background: "#22c55e", color: "white", padding: "4px 12px", borderRadius: "20px", fontWeight: "600" }}>OPEN NOW</span>}
              </div>
              <div style={styles.storeStatus}>
                <FaMapPin style={{ fontSize: "14px" }} /> {data.city}, {data.state} 
                <span style={{ margin: "0 8px", opacity: 0.5 }}>•</span>
                <FaClock style={{ fontSize: "14px" }} /> {data.shiftStart} - {data.shiftEnd}
              </div>
              <div style={styles.storeRating}>
                {[...Array(5)].map((_, i) => <FaStar key={i} style={{ color: i < Math.round(stats.rating) ? "#fbbf24" : "rgba(255,255,255,0.3)", fontSize: "14px" }} />)}
                <span style={{ marginLeft: "8px", fontWeight: "600" }}>{stats.rating.toFixed(1)}</span>
                <span style={{ opacity: 0.7 }}>({stats.totalOrders} reviews)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div style={styles.statsRow}>
          <div style={styles.statItem}>
            <div style={styles.statValue}>₹{stats.totalRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            <div style={styles.statLabel}>Total Revenue</div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statValue}>{stats.totalOrders}</div>
            <div style={styles.statLabel}>Completed Orders</div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statValue}>{getKycProgress()}%</div>
            <div style={styles.statLabel}>Profile Complete</div>
          </div>
        </div>

        {/* Desktop 2-Column Grid */}
        <div style={styles.dashboardGrid}>
          
          {/* Left Column: Management */}
          <div style={styles.card}>
            {/* KYC Banner */}
            <div style={styles.kycBanner} onClick={() => handleNavigate('/dashboard/VerificationScreen')}>
              <div style={{ ...styles.kycProgress, width: data.isApproved ? "100%" : `${getKycProgress()}%` }}></div>
              <div style={{ display: "flex", alignItems: "center", gap: "16px", zIndex: 1 }}>
                <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem", color: data.isApproved ? "#22c55e" : theme.primary, boxShadow: theme.shadow }}>
                  {data.isApproved ? <FaCheckCircle /> : <FaIdCard />}
                </div>
                <div>
                  <div style={{ fontWeight: "600", fontSize: "16px", color: theme.text, marginBottom: "4px" }}>
                    {data.isApproved ? "KYC Successfully Verified" : "Complete KYC Verification"}
                  </div>
                  <div style={{ fontSize: "13px", color: theme.textSecondary }}>
                    {data.isApproved ? "All documents are approved and active." : `${getKycProgress()}% complete - Verify documents to enable payouts`}
                  </div>
                </div>
              </div>
              <span style={{ color: "white", fontSize: "13px", fontWeight: "600", padding: "8px 16px", borderRadius: "8px", background: data.isApproved ? "#22c55e" : theme.primary, display: "flex", alignItems: "center", gap: "6px", zIndex: 1 }}>
                {data.isApproved ? <><FaCheckCircle style={{ fontSize: "12px" }} /> Verified</> : <><FaArrowRight style={{ fontSize: "12px" }} /> Verify Now</>}
              </span>
            </div>

            <div style={styles.sectionTitle}>
              <span style={styles.sectionTitleLeft}><FaStore style={{ color: theme.primary }} /> Store Management</span>
            </div>

            {[
              { id: 'profile', icon: <FaUserCircle />, iconStyle: styles.menuIconPrimary, label: 'Show Your Profile', desc: `Managed by ${data.ownerName}`, action: 'View', actionIcon: <FaEye />, path: '/dashboard/RestaurantProfile' },
              { id: 'menu', icon: <FaUtensils />, iconStyle: styles.menuIconSecondary, label: 'Menu Update', desc: 'Change complete menu or pricing', action: 'Update', actionIcon: <FaPen />, path: '/dashboard/MenuUpdate' },
              { id: 'address', icon: <FaMapMarkerAlt />, iconStyle: styles.menuIconSuccess, label: 'Store Address', desc: data.address || 'Update pickup location', action: 'Update', actionIcon: <FaPen />, path: 'address' },
            ].map((item) => (
              <button
                key={item.id}
                style={{
                  ...styles.menuItem,
                  backgroundColor: hoveredItem === item.id ? theme.hoverBg : 'transparent',
                  borderColor: hoveredItem === item.id ? theme.border : 'transparent',
                }}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => item.path === 'address' ? handleAddressEdit() : handleNavigate(item.path)}
              >
                <div style={styles.menuLeft}>
                  <div style={{ ...styles.menuIconWrapper, ...item.iconStyle }}>{item.icon}</div>
                  <div>
                    <div style={styles.menuLabel}>{item.label}</div>
                    <div style={styles.menuDesc}>{item.desc}</div>
                  </div>
                </div>
                <span style={styles.menuAction}>{item.actionIcon} {item.action}</span>
              </button>
            ))}
          </div>

          {/* Right Column: Info & Support */}
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            
            {/* Contact Card */}
            <div style={styles.card}>
              <div style={styles.sectionTitle}>
                <span style={styles.sectionTitleLeft}><FaPhone style={{ color: theme.primary }} /> Contact & Info</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={styles.infoItem}>
                  <FaPhone style={{ color: theme.primary, fontSize: "16px" }} />
                  <div>
                    <div style={{ fontSize: "12px", color: theme.textSecondary, marginBottom: "2px" }}>Phone</div>
                    <div style={{ fontSize: "15px", fontWeight: "600", color: theme.text }}>{data.phone || 'N/A'}</div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <FaEnvelope style={{ color: theme.primaryDark, fontSize: "16px" }} />
                  <div>
                    <div style={{ fontSize: "12px", color: theme.textSecondary, marginBottom: "2px" }}>Email</div>
                    <div style={{ fontSize: "15px", fontWeight: "600", color: theme.text, wordBreak: "break-all" }}>{data.email || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Support Card */}
            <div style={styles.card}>
              <div style={styles.sectionTitle}>
                <span style={styles.sectionTitleLeft}><FaQuestionCircle style={{ color: theme.primary }} /> Support</span>
              </div>
              
              <div style={styles.supportCard} onClick={() => handleNavigate('/dashboard/HelpSupport')}>
                <div style={{ color: theme.primary, fontSize: "20px", marginBottom: "8px" }}><FaBookOpen /></div>
                <div style={{ fontSize: "14px", fontWeight: "600", color: theme.text, marginBottom: "4px" }}>Help & FAQ</div>
                <div style={{ fontSize: "12px", color: theme.textSecondary }}>Quick guides and documentation</div>
              </div>

              <div style={styles.supportCard} onClick={() => handleNavigate('/dashboard/MOUAgreement')}>
                <div style={{ color: theme.primaryDark, fontSize: "20px", marginBottom: "8px" }}><FaHandshake /></div>
                <div style={{ fontSize: "14px", fontWeight: "600", color: theme.text, marginBottom: "4px" }}>MOU Agreement</div>
                <div style={{ fontSize: "12px", color: theme.textSecondary }}>{data.isMouAccepted ? '✓ Signed and Active' : 'View & sign terms'}</div>
              </div>

              <div style={{ ...styles.supportCard, marginBottom: 0, borderColor: isDark ? 'rgba(239, 68, 68, 0.2)' : '#fecaca', backgroundColor: isDark ? 'rgba(239, 68, 68, 0.05)' : '#fef2f2' }} onClick={() => handleNavigate('/dashboard/SupportTicket')}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "8px" }}>
                  <FaTicketAlt style={{ color: "#ef4444", fontSize: "20px" }} />
                  <span style={{ backgroundColor: "#ef4444", color: "white", fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "12px" }}>{openTicketCount} OPEN</span>
                </div>
                <div style={{ fontSize: "14px", fontWeight: "600", color: theme.text, marginBottom: "4px" }}>Support Tickets</div>
                <div style={{ fontSize: "12px", color: theme.textSecondary }}>Track your active queries</div>
              </div>

              <button style={styles.logoutBtn} onClick={handleLogout}>
                <FaSignOutAlt /> Log Out from Dashboard
              </button>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", color: theme.textSecondary, fontSize: "13px", fontWeight: "500", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "16px" }}>
          <FaMobileAlt style={{ opacity: 0.6 }} /> Merchant Dashboard · v2.1.0 (Build 104)
        </div>

      </div>

      {/* 🔥 Logo Preview Modal */}
      {logoPreview && (
        <div style={styles.modalOverlay} onClick={closeLogoPreview}>
          <button 
            style={styles.modalClose} 
            onClick={closeLogoPreview}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            <FaTimes />
          </button>
          <img 
            src={logoPreview} 
            alt="Store Logo Preview" 
            style={styles.modalImage}
            onError={(e) => {
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjZjk3MzE2Ij48cGF0aCBkPSJNMTIgMkwgMiA3bDEwIDUgMTAtNS0xMC01ek0yIDE3bDEwIDUgMTAtNU0yIDEybDEwIDUgMTAtNSIvPjwvc3ZnPg==';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ProfileCard;
